'use client'

import { MessageKeys, useTranslations } from "next-intl"
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ProductsSection } from "../sections/products-section"
import XIcon from '@/components/icons/x-icon'
import { formatAsKey } from "@/lib/i18n/key-format"

interface CategoriesFiltersProps {
  categories: {
    name: string;
    id: string;
    count: number;
  }[];
  className?: string;
}

export function CategoriesFilters({ categories, className }: CategoriesFiltersProps) {
  const tc = useTranslations('Categories')
  const router = useRouter()
  const searchParams = useSearchParams()

  // Get current selected category from URL
  const getCurrentCategory = useCallback(() => {
    return searchParams.get('category_id') || null
  }, [searchParams])

  // Set category filter or clear it if clicking on the same category
  const handleCategoryClick = useCallback((categoryId: string) => {
    const params = new URLSearchParams(searchParams)
    const currentCategory = getCurrentCategory()

    // If user clicked on a different category than current, set new category_id
    if (currentCategory !== categoryId) {
      if (categoryId) {
        // new category require new filters 
        params.delete('filter_by')
        params.set('category_id', categoryId)
      } else {
        params.delete('category_id')
      }
    } else {
      // If clicking the same category, remove the filter
      params.delete('category_id')
    }

    // Reset to page 1
    params.delete('page')

    router.push(`?${params.toString()}#products`)
  }, [router, searchParams, getCurrentCategory])

  // Clear category selection
  const handleClearCategory = useCallback(() => {
    const params = new URLSearchParams(searchParams)
    params.delete('category_id')
    params.delete('page')
    router.push(`?${params.toString()}#products`)
  }, [router, searchParams])

  const currentCategory = getCurrentCategory()

  const displayCategories = categories.length > 0 ? categories : []

  return (
    <ProductsSection className={className}>
      <div className="overflow-x-auto pb-2 -mx-4 px-4">
        <div className={cn("flex flex-nowrap gap-3 min-w-max", className)}>
          {displayCategories.map((category) => {
            // Try to get translated category name, fall back to provided name or ID
            const categoryName = category.name
            const isSelected = category.id === currentCategory || (category.id === 'all' && !currentCategory)

            return (
              <Button
                key={category.id}
                variant={isSelected ? "default" : "outline"}
                onClick={() => handleCategoryClick(category.id === 'all' ? '' : category.id)}
                className="px-6 py-2 h-auto rounded-none text-base font-normal flex items-center gap-2 shrink-0"
              >
                {tc(formatAsKey(categoryName) as MessageKeys<typeof tc, keyof typeof tc>)} ({category.count})
                {isSelected && (
                  <span className="ml-1" onClick={(e) => {
                    e.stopPropagation();
                    handleClearCategory();
                  }}>
                    <XIcon />
                  </span>
                )}
              </Button>
            )
          })}
        </div>
      </div>
    </ProductsSection>
  )
}